import type { ConnectorHandle } from "@skaile/workspaces/connectors";
import type { FlowExecution, NodeError } from "@skaile/workspaces/types";
import { FlowAdapter } from "./adapter.js";
import type { FlowDefinition } from "./engine/types.js";
import type { NodeExecutor } from "./node-executor.js";
import type { TurnStimulus } from "./prompt-fragments.js";
import type { SubpromptRunner } from "./subprompt-runner.js";
import type {
  InlineSubFlowOperationTarget,
  InlineSubFlowProjection,
  InlineSubFlowRequest,
  InlineSubFlowResult,
  InlineSubFlowRunner,
} from "./sub-flow-runner.js";

/** Maximum number of flow identities allowed in one transient inline ancestry. */
export const INLINE_SUB_FLOW_MAX_DEPTH = 16;

/** One agent turn driven by the already-materialized parent session. */
export interface InlineSubFlowAgentTurn {
  /** Child adapter whose agent nodes use the parent session's connector tools. */
  adapter: FlowAdapter;
  /** Connected child handle. */
  handle: ConnectorHandle;
  /** Child definition with its resolved parent-bound input applied. */
  flow: FlowDefinition;
  /** Live child snapshot used to build the normal orchestrator prompt. */
  execution: FlowExecution;
  /** Child-local stimulus that initiated this turn. */
  stimulus: TurnStimulus;
}

/** Host dependencies for driving an inline child through the existing FlowAdapter lifecycle. */
export interface SessionInlineSubFlowRunnerOptions {
  /** Resolves a flow identity through the host's normal installed/manual/asset lookup. */
  resolveFlow(flowId: string): FlowDefinition | Promise<FlowDefinition>;
  /** Runs one child agent turn with the parent session's existing driver. */
  driveAgentTurn(turn: InlineSubFlowAgentTurn): Promise<void>;
  /** Reuses the materialized session workspace for deterministic child nodes. */
  nodeExecutor?: NodeExecutor;
  /** Reuses the parent's isolated-driver seam for child subprompt nodes. */
  subpromptRunner?: SubpromptRunner;
}

interface InlineChildSession {
  key: string;
  request: InlineSubFlowRequest;
  adapter: FlowAdapter;
  handle: ConnectorHandle;
  flow: FlowDefinition;
  target: InlineSubFlowOperationTarget;
  stimulus: TurnStimulus;
  changeVersion: number;
  agentTurnActive: boolean;
  closed: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function childDefinition(request: InlineSubFlowRequest, authored: FlowDefinition): FlowDefinition {
  const childRunInput = authored.defaults?.run_input;
  const parentRunInput = request.parentFlow.defaults?.run_input;
  const hasBoundInputs = Object.keys(request.inputs).length > 0;
  const hasParentContext = request.passContext && parentRunInput !== undefined;
  if (!hasBoundInputs && !hasParentContext) return authored;

  let runInput: unknown;
  if (
    (childRunInput === undefined || isRecord(childRunInput)) &&
    (!hasParentContext || isRecord(parentRunInput))
  ) {
    runInput = {
      ...(isRecord(childRunInput) ? childRunInput : {}),
      ...(hasParentContext && isRecord(parentRunInput) ? parentRunInput : {}),
      ...request.inputs,
    };
  } else if (hasBoundInputs) {
    runInput = request.inputs;
  } else {
    runInput = parentRunInput;
  }

  return {
    ...authored,
    defaults: {
      ...authored.defaults,
      run_input: runInput as NonNullable<FlowDefinition["defaults"]>["run_input"],
    },
  };
}

function terminalError(execution: FlowExecution): NodeError {
  const failed = Object.values(execution.nodes).find((node) => node.status === "failed");
  if (failed?.error) return failed.error;
  return {
    message: `Inline child flow ${execution.flowId} ended with status ${execution.status}`,
    recoverable: false,
    at: new Date().toISOString(),
  };
}

function projection(session: InlineChildSession): InlineSubFlowProjection {
  const execution = session.adapter.getExecution(session.handle);
  for (const definition of session.flow.nodes) {
    const node = execution.nodes[definition.id];
    if (node?.status === "awaiting_approval" && node.approval?.decision == null) {
      return { status: "awaiting_approval", approval: structuredClone(node.approval) };
    }
    if (node?.status === "awaiting_input" && node.input?.providedAt == null) {
      return { status: "awaiting_input", input: structuredClone(node.input) };
    }
  }
  const approvals = Object.values(execution.nodes)
    .flatMap((node) => node.approvalHistory)
    .sort((left, right) => left.requestedAt.localeCompare(right.requestedAt));
  const inputs = Object.values(execution.nodes)
    .flatMap((node) => node.inputHistory)
    .sort((left, right) => left.requestedAt.localeCompare(right.requestedAt));
  return {
    status: "running",
    approval: approvals.at(-1) ? structuredClone(approvals.at(-1)) : undefined,
    input: inputs.at(-1) ? structuredClone(inputs.at(-1)) : undefined,
  };
}

function currentInteractionNode(
  session: InlineChildSession,
  status: "awaiting_approval" | "awaiting_input",
): string {
  const execution = session.adapter.getExecution(session.handle);
  const node = session.flow.nodes.find(
    (definition) => execution.nodes[definition.id]?.status === status,
  );
  if (!node) {
    throw new Error(`Inline child ${session.flow.id} is not ${status.replaceAll("_", " ")}`);
  }
  return node.id;
}

/**
 * Drives delegated flows once, inline, through child FlowAdapters in the current session.
 *
 * Child sessions are transient and keyed by their owning parent node. A parked/no-op child stays
 * connected in memory for the next parent stimulus, but no child snapshot is published or stored.
 */
export class SessionInlineSubFlowRunner implements InlineSubFlowRunner {
  private readonly sessions = new Map<string, InlineChildSession>();

  constructor(private readonly options: SessionInlineSubFlowRunnerOptions) {}

  private sessionKey(request: InlineSubFlowRequest): string {
    return `${request.parentExecution.runId}\u0000${request.node.id}`;
  }

  private async dispose(session: InlineChildSession): Promise<void> {
    if (session.closed) return;
    session.closed = true;
    this.sessions.delete(session.key);
    session.request.routeOperationsTo(undefined);
    await session.adapter.disconnect(session.handle);
  }

  private async cancel(session: InlineChildSession): Promise<void> {
    if (session.closed) return;
    try {
      await session.adapter.executeOp(session.handle, "cancel", {});
    } finally {
      await this.dispose(session);
    }
  }

  private async createSession(request: InlineSubFlowRequest): Promise<InlineChildSession> {
    if (request.ancestry.includes(request.flowId)) {
      throw new Error(
        `Inline sub-flow cycle detected: ${[...request.ancestry, request.flowId].join(" -> ")}`,
      );
    }
    if (request.ancestry.length >= INLINE_SUB_FLOW_MAX_DEPTH) {
      throw new Error(
        `Inline sub-flow depth exceeds ${INLINE_SUB_FLOW_MAX_DEPTH}: ${[
          ...request.ancestry,
          request.flowId,
        ].join(" -> ")}`,
      );
    }

    const authored = await this.options.resolveFlow(request.flowId);
    if (request.ancestry.includes(authored.id)) {
      throw new Error(
        `Inline sub-flow cycle detected after resolving ${request.flowId}: ${[
          ...request.ancestry,
          authored.id,
        ].join(" -> ")}`,
      );
    }
    const flow = childDefinition(request, authored);
    const adapter = new FlowAdapter();
    const handle = await adapter.connect(
      {
        id: `flow:${request.parentExecution.runId}:${request.node.id}`,
        driver: "flow",
        access: "read-write",
        options: {
          flow,
          flowInputOrigins: request.inputOrigins,
          inlineSubFlowAncestry: [...request.ancestry, flow.id],
          inlineSubFlowRunner: this,
          nodeExecutor: this.options.nodeExecutor,
          subpromptRunner: this.options.subpromptRunner,
          seed: {
            runId: `${request.parentExecution.runId}:${request.node.id}`,
            startedBy: request.parentExecution.startedBy,
            autonomousMode: request.parentExecution.autonomousMode,
          },
        },
      },
      {},
    );

    const key = this.sessionKey(request);
    let session: InlineChildSession;
    const target: InlineSubFlowOperationTarget = {
      parentNodeId: request.node.id,
      isAgentTurnActive: () => session.agentTurnActive || adapter.hasActiveInlineAgentTurn(handle),
      executeAgentOp: (operation, args) => adapter.executeOp(handle, operation, args),
      applyParentApproval: async (args) => {
        const nodeId = currentInteractionNode(session, "awaiting_approval");
        await adapter.executeOp(handle, "applyApproval", { ...args, nodeId });
        session.stimulus = {
          kind: "approval_received",
          nodeId,
          decision: args.decision === "rejected" ? "rejected" : "approved",
          feedback: typeof args.feedback === "string" ? args.feedback : undefined,
          decidedBy: String(args.decidedBy ?? ""),
        };
        return projection(session);
      },
      applyParentInput: async (args) => {
        const nodeId = currentInteractionNode(session, "awaiting_input");
        await adapter.executeOp(handle, "applyInput", { ...args, nodeId });
        session.stimulus = {
          kind: "input_received",
          nodeId,
          response: args.response,
          providedBy: String(args.providedBy ?? ""),
        };
        return projection(session);
      },
      cancel: () => this.cancel(session),
      getExecution: () => adapter.getAgentExecution(handle),
      getMandatoryAgentApprovalNodeIds: () => adapter.getMandatoryAgentApprovalNodeIds(handle),
    };
    session = {
      key,
      request,
      adapter,
      handle,
      flow,
      target,
      stimulus: { kind: "flow_started" },
      changeVersion: 0,
      agentTurnActive: false,
      closed: false,
    };
    adapter.onStateChange(handle, () => {
      session.changeVersion += 1;
    });
    this.sessions.set(key, session);
    request.routeOperationsTo(target);
    return session;
  }

  async run(request: InlineSubFlowRequest): Promise<InlineSubFlowResult> {
    const key = this.sessionKey(request);
    const session = this.sessions.get(key) ?? (await this.createSession(request));
    session.request = request;
    request.routeOperationsTo(session.target);

    try {
      while (!session.closed) {
        await session.adapter.drainRuntimeNodes(session.handle);
        const execution = session.adapter.getExecution(session.handle);
        if (execution.done || execution.status === "complete") {
          await this.dispose(session);
          return {
            status: "complete",
            output: {
              summary: `Inline child flow ${session.flow.id} completed`,
              artifacts: [],
            },
          };
        }
        if (execution.status === "failed" || execution.status === "cancelled") {
          const error = terminalError(execution);
          await this.dispose(session);
          return { status: "failed", error };
        }

        if (session.adapter.shouldDriveAgentTurn(session.handle, session.stimulus)) {
          const versionBeforeTurn = session.changeVersion;
          session.agentTurnActive = true;
          try {
            await this.options.driveAgentTurn({
              adapter: session.adapter,
              handle: session.handle,
              flow: session.flow,
              execution,
              stimulus: session.stimulus,
            });
          } finally {
            session.agentTurnActive = false;
          }
          if (session.closed) {
            return { status: "failed", error: terminalError(execution) };
          }
          session.stimulus = { kind: "state_changed" };
          if (session.changeVersion === versionBeforeTurn) {
            return { status: "paused", projection: projection(session) };
          }
          continue;
        }

        return { status: "paused", projection: projection(session) };
      }

      return {
        status: "failed",
        error: {
          message: `Inline child flow ${request.flowId} was cancelled`,
          recoverable: false,
          at: new Date().toISOString(),
        },
      };
    } catch (error) {
      try {
        await this.dispose(session);
      } catch {
        // Session ownership and routing were cleared before adapter disconnect attempted cleanup.
      }
      throw error;
    }
  }
}
